import type { TBotConfig } from '~entities/bot';
import { chatEntity } from '~entities/chats/chatEntity';
import { gridApi } from '~services/api/grid';
import { api, apiTags } from '~shared/api/origin';
import { isSafeApp } from '~shared/utils/host';
import type { RootState } from '~store/index';
import { sortChats } from '~store/utils/sortChats';

import { dialogsApi, messagesAdapter } from '../dialogs';
import { userApi } from '../user';
import type {
	BuyBotGalleryMediaApiResponse,
	ChatsAnchor,
	GetBotAccessInfoApiResponse,
	GetBotGalleryApiResponse,
	GetBotModelsApiResponse,
	GetBotProfileStatsApiResponse,
	GetChatApiResponse,
	GetChatApiTransformedResponse,
	GetChatsApiRequestParams,
	GetChatsApiResponse,
	GetChatsApiTransformedResponse,
	GetChatsCountApiResponse,
	GetNextBotsApiResponse,
} from './types';
import { transformChatsMessage } from './utils';

export const {
	endpoints,
	util,
	useGetChatsQuery,
	usePinChatMutation,
	useUnpinChatMutation,
	useDeleteChatMutation,
	useBlockBotMutation,
	useUnblockBotMutation,
	useGetChatQuery,
	useGetNextBotsQuery,
	useGetChatsCountQuery,
	useReportAndDeleteBotMutation,
	useChangeBotAccessMutation,
	useGetBotAccessInfoQuery,
	useGetBotProfileQuery,
	useLazyGetBotProfileQuery,
	useGetBotModelsQuery,
	useSetBotModelMutation,
	useGetBotStatsQuery,
	useGetBotGalleryQuery,
	useBuyMediaMutation,
} = api.injectEndpoints({
	endpoints: (build) => ({
		getChats: build.query<
			GetChatsApiTransformedResponse,
			GetChatsApiRequestParams
		>({
			query: ({ userId, select = 20, from = [undefined, true], search }) => ({
				url: `/ai/chats/web/${userId}${search ? '/search' : ''}`,
				params: {
					BotName: search,
					select,
					lastEventAtTo: from[0],
					isNsfw: isSafeApp ? false : undefined,
					isPinned: from[1],
				},
			}),
			transformResponse: (
				response: GetChatsApiResponse | null,
				_,
				{ from = [undefined, true] }
			) => {
				if (response === null) {
					return {
						chats: [],
						hasMoreRows: false,
						next: from,
					};
				}

				const pinned = from[1];
				let { hasMoreRows } = response;
				const chats = response.chats.map(({ lastMessage, ...chat }) => ({
					...chat,
					avatar: chat.bot.images?.chatAvatarLinks,
					avatarFallback: chat.bot.images?.chatAvatarPath,
					lastMessage: lastMessage && transformChatsMessage(lastMessage),
					isTyping: false,
					pinned,
					blocked: chat.features?.includes('blocked') || false,
				}));
				const lastEventAt = chats[chats.length - 1]?.lastEventAt;
				let next: ChatsAnchor = [lastEventAt, pinned];

				if (!hasMoreRows && pinned) {
					next = [undefined, false];
					hasMoreRows = true;
				}

				return {
					chats,
					hasMoreRows,
					next,
				};
			},
			serializeQueryArgs: ({ endpointName, queryArgs }) => {
				return `${endpointName}-${queryArgs?.search || false}`;
			},
			forceRefetch: ({ currentArg, endpointState }) => {
				const state = endpointState?.data as
					| GetChatsApiTransformedResponse
					| undefined;
				const from = currentArg?.from;
				const next = state?.next;
				const fromPinned = from?.[1];
				const nextPinned = next?.[1];

				if (fromPinned !== nextPinned) {
					return true;
				}

				const fromEventAt = from?.[0];
				const nextEventAt = next?.[0];

				return Boolean(
					!nextEventAt ||
						(fromEventAt &&
							new Date(fromEventAt).getTime() <=
								new Date(nextEventAt).getTime())
				);
			},
			merge: (currentState, incomingState) => {
				currentState.hasMoreRows = incomingState.hasMoreRows;
				currentState.next = incomingState.next;

				incomingState.chats.forEach((item) => {
					const existedIndex = currentState.chats.findIndex(
						({ uid }) => uid === item.uid
					);
					if (existedIndex > -1) {
						currentState.chats[existedIndex] = item;
					} else {
						currentState.chats.push(item);
					}
				});

				sortChats(currentState.chats);
			},
			providesTags: [apiTags.chats],
		}),
		getChat: build.query<
			GetChatApiTransformedResponse,
			{
				userId: string;
				recipientId: string;
			}
		>({
			queryFn: async (
				{ userId, recipientId },
				{ dispatch, getState },
				_extraOptions,
				fetchWithBQ
			) => {
				const { data: chatsData } = endpoints.getChats.select({
					userId,
				})(getState() as RootState) as { data: GetChatsApiTransformedResponse };
				const chats = chatsData?.chats || [];
				let chat = chats.find((chat) => chat.botId === recipientId);

				if (!chat) {
					const response = await fetchWithBQ(
						`/ai/chats/${userId}/${recipientId}`
					);
					let data: GetChatApiResponse;

					if (response.error || response.meta?.response?.status === 204) {
						const response = await fetchWithBQ(
							`/ai/bots/${userId}/${recipientId}/profile`
						);

						if (response.error) {
							return { error: response.error };
						}

						const bot = response.data as TBotConfig;

						data = {
							botId: String(bot.clientId),
							uid: bot.uid,
							bot,
							lastEventAt: '',
							counters: {
								unreadCount: 0,
								totalCount: 0,
							},
							features: [],
						};
					} else {
						data = response.data as GetChatApiResponse;
					}

					chat = {
						...data,
						avatar: data.bot.images?.chatAvatarLinks,
						avatarFallback: data.bot.images?.chatAvatarPath,
						lastMessage:
							data.lastMessage && transformChatsMessage(data.lastMessage),
						isTyping: false,
						// do not show chat to not confuse user with unexpected insertion of chat in a list
						// will be shown on chats pagination
						isHidden: true,
						blocked: data.features?.includes('blocked') || false,
					};

					chatEntity.updateCollection(getState, dispatch, (chats) => {
						chats.push(chat!);
					});
				}

				return { data: chat };
			},
			forceRefetch: () => true,
			providesTags: [apiTags.chats],
		}),
		pinChat: build.mutation<
			{},
			{
				userId: string;
				id: string;
			}
		>({
			query: ({ userId, id }) => ({
				url: `/ai/chats/features/${userId}/pin`,
				method: 'POST',
				body: {
					botIds: [id],
				},
			}),
			async onQueryStarted({ id }, { getState, dispatch, queryFulfilled }) {
				try {
					await queryFulfilled;

					chatEntity.update(getState, dispatch, (chat) => {
						if (chat.botId === id) {
							chat.pinned = true;
						}
					});
					chatEntity.updateCollection(getState, dispatch, (chats) => {
						const chat = chats.find((chat) => chat.botId === id);

						if (chat) {
							chats.splice(chats.indexOf(chat), 1);
							chats.unshift(chat);
						}
					});
				} catch {}
			},
		}),
		unpinChat: build.mutation<
			{},
			{
				userId: string;
				id: string;
			}
		>({
			query: ({ userId, id }) => ({
				url: `/ai/chats/features/${userId}/unpin`,
				method: 'POST',
				body: {
					botIds: [id],
				},
			}),
			async onQueryStarted({ id }, { getState, dispatch, queryFulfilled }) {
				try {
					await queryFulfilled;

					chatEntity.update(getState, dispatch, (chat) => {
						if (chat.botId === id) {
							chat.pinned = false;
						}
					});
					chatEntity.updateCollection(getState, dispatch, (chats) => {
						const unpinnedChat = chats.find((chat) => chat.botId === id);

						if (unpinnedChat) {
							sortChats(chats);
						}
					});
				} catch {}
			},
		}),
		deleteChat: build.mutation<{}, { userId: string; id: string }>({
			query: ({ userId, id }) => ({
				url: `/ai/chats/features/${userId}/remove`,
				method: 'POST',
				body: {
					botIds: [id],
				},
			}),
			invalidatesTags: (result, error, { id }) => [
				{ type: apiTags.messages, id },
			],
			async onQueryStarted(
				{ userId, id },
				{ getState, dispatch, queryFulfilled }
			) {
				try {
					await queryFulfilled;

					chatEntity.updateCollection(getState, dispatch, (chats) => {
						const chatIndex = chats.findIndex((chat) => chat.botId === id);

						if (chatIndex > -1) {
							chats.splice(chatIndex, 1);
						}
					});
					dispatch(
						dialogsApi.util.updateQueryData(
							'getMessages',
							{ id: userId, recipientId: id, omit: 0 },
							(draft) => {
								draft.totalLength = 0;
								draft.receivedCount = 0;
								draft.messages = messagesAdapter.getInitialState();
							}
						)
					);
				} catch {}
			},
		}),
		blockBot: build.mutation<{}, { userId: string; id: string }>({
			query: ({ userId, id }) => ({
				url: `/ai/chats/features/${userId}/block`,
				method: 'POST',
				body: {
					botIds: [id],
				},
			}),
			async onQueryStarted({ id }, { getState, dispatch, queryFulfilled }) {
				try {
					await queryFulfilled;

					chatEntity.update(getState, dispatch, (chat) => {
						if (chat.botId === id) {
							chat.blocked = true;
						}
					});
				} catch {}
			},
		}),
		unblockBot: build.mutation<{}, { userId: string; id: string }>({
			query: ({ userId, id }) => ({
				url: `/ai/chats/features/${userId}/unblock`,
				method: 'POST',
				body: {
					botIds: [id],
				},
			}),
			async onQueryStarted({ id }, { getState, dispatch, queryFulfilled }) {
				try {
					await queryFulfilled;

					chatEntity.update(getState, dispatch, (chat) => {
						if (chat.botId === id) {
							chat.blocked = false;
						}
					});
				} catch {}
			},
		}),
		getNextBots: build.query<
			GetNextBotsApiResponse,
			{
				userId: string;
			}
		>({
			query: ({ userId }) => ({
				url: `/ai/nextbots/${userId}`,
				params: {
					isNsfw: isSafeApp ? false : undefined,
				},
			}),
			transformResponse: (response: GetNextBotsApiResponse | null) => {
				if (!response) {
					return {
						bots: [],
					};
				}

				const bots = response.bots.map((bot) => {
					const preCondition = bot.conditions?.preCondition;
					const paymentOptionLevel =
						preCondition?.paymentOptions.find(
							(option) => option.type === 'level'
						)?.value || 0;

					return {
						...bot,
						paymentOptionLevel,
					};
				});

				return {
					bots,
				};
			},
			providesTags: [apiTags.nextBots],
		}),
		getChatsCount: build.query<
			GetChatsCountApiResponse,
			{
				userId: string;
			}
		>({
			query: ({ userId }) => ({
				url: `/ai/chats/${userId}/count`,
			}),
		}),
		reportAndDeleteBot: build.mutation<
			{},
			{
				userId: string;
				recipientId: string;
				reason: string;
				reportText: string;
			}
		>({
			query: ({ userId, reportText, recipientId, reason }) => ({
				url: `/ai/bots/${userId}/${recipientId}/reports`,
				method: 'POST',
				body: {
					reason,
					comment: reportText,
				},
			}),
			async onQueryStarted(
				{ recipientId },
				{ dispatch, queryFulfilled, getState }
			) {
				try {
					await queryFulfilled;

					chatEntity.updateCollection(getState, dispatch, (chats) => {
						const chatIndex = chats.findIndex(
							({ botId }) => botId === recipientId
						);

						if (chatIndex > -1) {
							chats.splice(chatIndex, 1);
						}
					});

					for (const { originalArgs } of gridApi.util.selectInvalidatedBy(
						getState(),
						[apiTags.grid]
					)) {
						dispatch(
							gridApi.util.updateQueryData('getGrid', originalArgs, (draft) => {
								draft.botGrid = draft.botGrid.filter(
									({ clientId }) => clientId !== +recipientId
								);
							})
						);
					}
				} catch {}
			},
		}),
		changeBotAccess: build.mutation<
			{},
			{ userId: string; recipientId: string; access: 'public' | 'private' }
		>({
			query: ({ userId, recipientId, access }) => ({
				url: `/ai/bots/${userId}/${recipientId}/profile`,
				method: 'PATCH',
				body: {
					access,
				},
			}),
			onQueryStarted({ userId, recipientId, access }, { getState, dispatch }) {
				dispatch(
					util.updateQueryData(
						'getBotProfile',
						{ userId, recipientId },
						(draft) => {
							if (draft) {
								draft.access = access;
							}
						}
					)
				);

				chatEntity.update(getState, dispatch, (chat) => {
					if (chat.bot.clientId.toString() === recipientId) {
						chat.bot.access = access;
					}
				});
			},
			invalidatesTags: [apiTags.grid],
		}),
		getBotAccessInfo: build.query<
			GetBotAccessInfoApiResponse,
			{ userId: string; recipientId: string }
		>({
			query: ({ userId, recipientId }) => ({
				url: `/ai/bots/${userId}/${recipientId}/access`,
			}),
		}),
		getBotProfile: build.query<
			TBotConfig | null,
			{ userId: string; recipientId: string | number }
		>({
			async queryFn(
				{ userId, recipientId },
				_queryApi,
				_extraOptions,
				fetchWithBQ
			) {
				const response = await fetchWithBQ(
					`/ai/bots/${userId}/${recipientId}/profile`
				);

				if (!response.error) {
					return { data: response.data as TBotConfig };
				} else if (
					response.error.status === 403 ||
					response.error.status === 404
				) {
					return { data: null };
				}
				return {
					error: response.error,
				};
			},
		}),
		getBotModels: build.query<GetBotModelsApiResponse, { userId: string }>({
			query: ({ userId }) => ({ url: `ai/models/${userId}` }),
		}),
		setBotModel: build.mutation<{}, { userId: string; code: string }>({
			query: ({ userId, code }) => ({
				url: `ai/models/${userId}/active`,
				method: 'PUT',
				body: { code },
			}),
			async onQueryStarted({ userId, code }, { dispatch, queryFulfilled }) {
				await queryFulfilled;
				dispatch(
					userApi.util.updateQueryData('userConfig', { userId }, (draft) => {
						draft.activeModel = code;
					})
				);
			},
			invalidatesTags: [apiTags.userConfig],
		}),
		getBotStats: build.query<
			GetBotProfileStatsApiResponse,
			{ userId: string; recipientId: string }
		>({
			query: ({ userId, recipientId }) => ({
				url: `/ai/bots/${userId}/${recipientId}/stats/v2`,
			}),
		}),
		getBotGallery: build.query<
			GetBotGalleryApiResponse,
			{
				userId: string;
				recipientId: string;
				page: number;
				pageSize?: number;
			}
		>({
			query: ({ userId, recipientId, page, pageSize = 28 }) => ({
				url: `/ai/${userId}/bot/gallery/${recipientId}`,
				params: {
					page,
					pageSize,
				},
			}),
			transformResponse: (
				response: GetBotGalleryApiResponse | null,
				_,
				{ page }
			) => {
				if (response === null) {
					return {
						currentPage: 0,
						receivedCount: 0,
						totalLength: 0,
						media: [],
					};
				}

				const { media } = response;

				return {
					media,
					currentPage: page,
					receivedCount: media.length,
					totalLength: media.length,
				};
			},
			serializeQueryArgs: ({ endpointName, queryArgs }) => {
				return `${endpointName}-${queryArgs?.userId}-${queryArgs?.recipientId}`;
			},
			forceRefetch: ({ currentArg, endpointState }) => {
				return (
					(currentArg?.page === 0 && (currentArg?.pageSize || 0) > 3) ||
					(currentArg?.page ?? 0) >
						((endpointState?.data as GetBotGalleryApiResponse | undefined)
							?.currentPage ?? 0)
				);
			},
			merge: (currentState, incomingState) => {
				if (incomingState.currentPage === 0) {
					Object.assign(currentState, incomingState);
					return;
				}

				currentState.currentPage = incomingState.currentPage;
				currentState.receivedCount = incomingState.receivedCount;
				currentState.totalLength =
					currentState.totalLength + incomingState.receivedCount;

				incomingState.media.forEach((item) => {
					const existedIndex = currentState.media.findIndex(
						({ mediaId, dialogMediaId }) =>
							(item.dialogMediaId !== undefined &&
								dialogMediaId === item.dialogMediaId) ||
							(item.mediaId !== undefined && mediaId === item.mediaId)
					);

					if (existedIndex > -1) {
						currentState.media[existedIndex] = item;
						currentState.totalLength -= 1;
					} else {
						currentState.media.push(item);
					}
				});
			},
			providesTags: [apiTags.botGallery],
		}),
		buyMedia: build.mutation<
			BuyBotGalleryMediaApiResponse,
			{
				userId: string;
				dialogMediaId: string;
				recipientId: string;
			}
		>({
			query: ({ userId, dialogMediaId }) => ({
				url: `/ai/${userId}/dialogs/media/buy`,
				method: 'PUT',
				body: { dialogMediaId },
			}),
			async onQueryStarted(
				{ userId, dialogMediaId, recipientId },
				{ dispatch, queryFulfilled }
			) {
				const { data } = await queryFulfilled;
				const newMedia = data.media;

				dispatch(
					util.updateQueryData(
						'getBotGallery',
						{ userId, recipientId, page: 0 },
						(draft) => {
							const mediaItem = draft.media.find(
								(item) => item.dialogMediaId === dialogMediaId
							);
							if (mediaItem) {
								Object.assign(mediaItem, newMedia);

								const { tariffication } = mediaItem;

								if (tariffication) {
									tariffication.access = true;
									tariffication.paid = true;
								}
							}
						}
					)
				);
			},
		}),
	}),
});

export const {
	getBotProfile: { useQueryState: useGetBotProfileQueryState },
	getChats: { useQueryState: useGetChatsQueryState },
} = endpoints;

chatEntity.addCollectionSource(
	util,
	'getChats',
	[apiTags.chats],
	(draft, pick) => pick(draft.chats)
);
chatEntity.addSingleSource(util, 'getChat', [apiTags.chats], (draft, pick) =>
	pick(draft)
);
